import React, { useState, useEffect, useRef } from "react";
import { auth, db } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { collection, getDocs, getCountFromServer } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import "./Dashboard.css";
import UsersPanel from "./UsersPanel";

const Dashboard = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState("Loading...");
  const [darkMode, setDarkMode] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [activeView, setActiveView] = useState("dashboard");
  const [totalUsers, setTotalUsers] = useState("Loading...");
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (darkMode) document.body.classList.add("dark-mode");
    else document.body.classList.remove("dark-mode");
  }, [darkMode]);

  useEffect(() => {
    const fetchUserCount = async () => {
      try {
        const coll = collection(db, "users");
        const snapshot = await getCountFromServer(coll);
        setTotalUsers(snapshot.data().count);
      } catch (error) {
        console.error("Error fetching user count:", error);
        setTotalUsers("Error");
      }
    };

    fetchUserCount();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserName(user.displayName || user.email);
      } else {
        setUserName("Guest");
        navigate("/login");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  const generateAllUsersReport = async () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("System User Report", 14, 22);
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Generated On: ${new Date().toLocaleString()}`, 14, 30);

    try {
      const querySnapshot = await getDocs(collection(db, "users"));
      const tableData = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const displayName = data.name || "Unknown User";
        const email = data.email || "No Email Provided";

        tableData.push([
          displayName,
          email,
          data.role || "User",
          doc.id
        ]);
      });

      autoTable(doc, {
        head: [["Name", "Email", "Role", "UID"]],
        body: tableData,
        startY: 35,
        theme: "striped",
        headStyles: { fillColor: [41, 128, 185] },
        styles: { fontSize: 10 },
      });

      doc.save("All_Users_Report.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate report.");
    }
  };

  const renderRightPanel = () => {
    switch (activeView) {
      case "dashboard":
        return (
          <section className="stats-grid">
            <div className="card">
              <h3>Total Users</h3>
              <p className="stat-value">{totalUsers}</p>
            </div>
            <div className="card">
              <h3>Monthly Revenue</h3>
              <p className="stat-value">$45,210</p>
            </div>
            <div className="card">
              <h3>Conversion Rate</h3>
              <p className="stat-value">3.2%</p>
            </div>
          </section>
        );
      case "users":
        return <UsersPanel />;
      default:
        return <div>Select an option</div>;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setReportsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="dashboard-container">
      <aside className="sidebar">
        <div className="sidebar-top">
          <div className="logo">Admin View</div>
          <nav className="sidebar-nav">
            <ul>
              <li
                className={activeView === "dashboard" ? "active" : ""}
                onClick={() => setActiveView("dashboard")}
              >
                📊 Dashboard
              </li>
              <li
                className={activeView === "users" ? "active" : ""}
                onClick={() => setActiveView("users")}
              >
                👥 Users
              </li>
            </ul>
          </nav>
        </div>
        <div className="sidebar-bottom">
          <nav className="sidebar-nav">
            <ul>
              <li className="logout-btn" onClick={handleLogout}>
                🚪 Logout
              </li>
            </ul>
          </nav>
        </div>
      </aside>

      <main className="main-content">
        <header className="top-bar">
          <h2>Welcome Back, {userName}</h2>
          <div className="top-bar-right">
            <div className="reports-dropdown" ref={dropdownRef}>
              <button
                className="reports-btn"
                onClick={() => setReportsOpen(!reportsOpen)}
              >
                📄 Reports ▾
              </button>
              <div className={`dropdown-menu ${reportsOpen ? "show" : ""}`}>
                <div className="dropdown-item" onClick={generateAllUsersReport}>
                  👥 All Users Report
                </div>
              </div>
            </div>
            <label className="theme-switch">
              <input
                type="checkbox"
                checked={darkMode}
                onChange={() => setDarkMode(!darkMode)}
              />
              <span className="slider"></span>
            </label>
          </div>
        </header>
        {renderRightPanel()}
      </main>
    </div>
  );
};

export default Dashboard;